import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { isAuthenticated, getAuthHeaders, getUserInfo, logout } from '../utils/auth';
import EmailSupportModal from './EmailSupportModal';
import './Sidebar.css';

export default function Sidebar({ onNavigateToUpload, onNavigateToHome, onNavigateToSessions }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showEmailSupportModal, setShowEmailSupportModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [canAccessCandidateMatching, setCanAccessCandidateMatching] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = isAuthenticated();
      // console.log('Sidebar - checking auth:', authenticated);
      setIsAuth(authenticated);
      setUserInfo(authenticated ? getUserInfo() : null);
    };

    checkAuth();
    
    // Listen for auth changes (including cookie auth verification)
    window.addEventListener('authChanged', checkAuth);
    window.addEventListener('storage', checkAuth);
    window.addEventListener('authHydrated', checkAuth);
    
    // Force a re-check after a short delay
    const timeout = setTimeout(checkAuth, 100);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('authChanged', checkAuth);
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authHydrated', checkAuth);
    };
  }, [location.pathname]); // Re-check when route changes

  const email = userInfo?.email || 'Guest';
  const userName = userInfo?.name || userInfo?.email?.split('@')[0] || 'Guest';

  const ADMIN_EMAILS = ['rajeshgowdankg9@gmail.com', 'guruprasad.prajvi@gmail.com'];
  const localAdminFallback = isAuth && ADMIN_EMAILS.includes(userInfo?.email?.toLowerCase());
  const isAdmin = hasAdminAccess;

  useEffect(() => {
    let cancelled = false;
    if (!isAuth) {
      setCanAccessCandidateMatching(false);
      setHasAdminAccess(false);
      return () => {
        cancelled = true;
      };
    }

    const checkCandidateMatchAccess = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.AUTH.CANDIDATE_MATCH_ACCESS_CHECK, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          setCanAccessCandidateMatching(res.ok);
          setHasAdminAccess(Boolean(data.admin));
        }
      } catch (_) {
        if (!cancelled) {
          setCanAccessCandidateMatching(localAdminFallback);
          setHasAdminAccess(localAdminFallback);
        }
      }
    };

    checkCandidateMatchAccess();
    return () => {
      cancelled = true;
    };
  }, [isAuth, localAdminFallback, userInfo?.email]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/');
    window.location.reload(); // Force refresh to update UI
  };

  const handleProtectedNavigation = (navigateFn, path) => {
    setIsMobileMenuOpen(false);
    if (!isAuth) {
      navigate('/signup');
      return;
    }
    navigateFn();
  };

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button 
        className="mobile-menu-toggle"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <span className="hamburger-icon">{isMobileMenuOpen ? '✕' : '☰'}</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

    <aside className={`bk-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
      <div className="bk-brand">
        <div className="bk-logo">
          <img src="/Blackkiteai_logo.png" alt="BlackKiteAI logo" />
        </div>
      </div>

      <nav className="bk-nav">
        <button className={`nav-item ${isActive('/') ? 'active' : ''}`} onClick={() => { setIsMobileMenuOpen(false); onNavigateToHome(); }}>
          <span className="icon">🏠</span>
          <span>Home</span>
        </button>
        <button 
          className={`nav-item ${isActive('/sessions') ? 'active' : ''}`} 
          onClick={() => handleProtectedNavigation(onNavigateToSessions, '/sessions')}
          title={!isAuth ? 'Sign in required' : 'Interview Sessions'}
        >
          <span className="icon">💬</span>
          <span>Interview Sessions</span>
        </button>
        <button 
          className={`nav-item ${isActive('/upload') ? 'active' : ''}`} 
          onClick={() => handleProtectedNavigation(onNavigateToUpload, '/upload')}
          title={!isAuth ? 'Sign in required' : 'CVs / Resumes'}
        >
          <span className="icon">📄</span>
          <span>CVs / Resumes</span>
        </button>
      </nav>

      <div className="bk-bottom">
        <div className="bk-links">
          <button
            className={`link ${isActive('/desktop-download') ? 'active' : ''}`}
            onClick={() => handleProtectedNavigation(() => navigate('/desktop-download'), '/desktop-download')}
          >
            ⬇️ Download Desktop App
          </button>
          <button
            className={`link ${isActive('/referral-history') ? 'active' : ''}`}
            onClick={() => handleProtectedNavigation(() => navigate('/referral-history'), '/referral-history')}
          >
            🎁 Referral Program
          </button>
          {isAdmin && (
            <button
              className={`link ${isActive('/email-campaigns') ? 'active' : ''}`}
              onClick={() => handleProtectedNavigation(() => navigate('/email-campaigns'), '/email-campaigns')}
            >
              📧 Email Campaigns
            </button>
          )}
          {isAdmin && (
            <button
              className={`link ${isActive('/job-management') ? 'active' : ''}`}
              onClick={() => handleProtectedNavigation(() => navigate('/job-management'), '/job-management')}
            >
              💼 Job Management
            </button>
          )}
          {isAdmin && (
            <button
              className={`link ${isActive('/internship-applications') ? 'active' : ''}`}
              onClick={() => handleProtectedNavigation(() => navigate('/internship-applications'), '/internship-applications')}
            >
              🎓 Internship Applications
            </button>
          )}
          {canAccessCandidateMatching && (
            <button
              className={`link candidate-match-link ${isActive('/candidate-matching') ? 'active' : ''}`}
              onClick={() => handleProtectedNavigation(() => navigate('/candidate-matching'), '/candidate-matching')}
            >
              <span className="candidate-ai-logo" aria-hidden="true">AI</span>
              <span>AI Candidate Matching</span>
            </button>
          )}
          <button
            className={`link ${isActive('/jobs') ? 'active' : ''}`}
            onClick={() => { setIsMobileMenuOpen(false); navigate('/jobs'); }}
          >
            💎 View Jobs
          </button>
          <button className="link" onClick={() => setShowEmailSupportModal(true)}>
            📧 Email Support
          </button>
          {isAuth && (
            <button className="link" onClick={handleLogout} style={{ color: '#ff6b6b' }}>
              🚪 Logout
            </button>
          )}
        </div>



        <div className="bk-user" onClick={() => { 
          setIsMobileMenuOpen(false); 
          if (isAuth) navigate('/profile'); 
        }} style={{ cursor: isAuth ? 'pointer' : 'default' }}>
          {isAuth ? (
            <>
              <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--brand-green)', marginBottom: '4px' }}>
                {userName}
              </div>
              <div style={{ fontSize: '12px', color: '#9fb1c8' }}>
                {email}
              </div>
            </>
          ) : (
            <div style={{ fontSize: '13px', color: '#9fb1c8' }}>
              Guest User
            </div>
          )}
        </div>
      </div>

      <EmailSupportModal 
        isOpen={showEmailSupportModal} 
        onClose={() => setShowEmailSupportModal(false)} 
      />

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="email-support-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowLogoutConfirm(false)}>
          <div className="email-support-modal">
            <button className="email-support-modal-close" onClick={() => setShowLogoutConfirm(false)}>
              ✕
            </button>
            
            <div className="email-support-icon" style={{ marginBottom: '1rem' }}>
              <img src="/Blackkiteai_logo.png" alt="BlackKiteAI logo" style={{ width: '80px', height: 'auto' }} />
            </div>
            
            <div className="email-support-icon" style={{ marginTop: '0.5rem' }}>
              <span style={{ fontSize: '48px' }}>🚪</span>
            </div>
            
            <h2 className="email-support-modal-title">Confirm Logout</h2>
            
            <p className="email-support-modal-message">
              Are you sure you want to logout?
            </p>
            
            <div className="email-support-modal-actions" style={{ gap: '12px' }}>
              <button className="btn-modal btn-modal-secondary" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button className="btn-modal btn-modal-primary btn-modal-success" onClick={confirmLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
    </>
  );
}
